import { ReactComponent as Activate } from "../../../../../assets/svg/activate.svg";
import requestCache from "../../../../../utils/requestCache";
import useBranchQueries from "../../../../school/hooks/useBranchQueries";
import {
  IProgramWizardConfigs,
  IWizardConfig,
  WizardMode,
  SubmitButtonColor,
} from "../../../../wizard/types";
import { parseToEditProgramInitialDataSecondary } from "../../../helpers/parseToEditProgramInitialDataSecondary";
import {
  mergeProgramSteps,
  parseToProgramBackEndModel,
} from "../../../helpers/parseToProgramBackEndModel";
import useProgramsQueriesSecondary from "../../../hooks/useProgramQueriesSecondary";
import { viewContentConfig } from "../view/viewContentConfig";
import { createConfigStep1 } from "../create/step1/config";
import { createConfigStep2 } from "../create/step2/config";
import { createConfigStep3 } from "../create/step3/config";
import { createConfigStep4 } from "../create/step4/config";
import { createConfigStep5 } from "../create/step5/config";
import { editConfigStep1 } from "../edit/step1/config";
import { editConfigStep2 } from "../edit/step2/config";
import { editConfigStep3 } from "../edit/step3/config";
import { editConfigStep4 } from "../edit/step4/config";
import { parseToProgramPreviewSecondary } from "../../../helpers/parseToProgramPreviewSecondary";
import { useSchoolId } from "../../../../school/hooks/useSchoolId";
import deactivateFormValidator from "../../validators/deactivateFormValidator";
import React from "react";
import DeactivateContent from "../../../../../components/DeactivateContent/DeactivateContent";
import DeleteContent from "../../../components/DeleteContent";
import { getDeleteButtonConfigs } from "../../../helpers/helperFunctions";
import { ENTITY_NAMES } from "../../../../../constants";

async function getCreateInitialData(academicId) {
  return {
    academicId,
  };
}

async function createProgram(formData, create) {
  const programFrontEndModel = mergeProgramSteps(formData);

  const academicBackEndModel = {
    id: formData.academicId,
    programs: [parseToProgramBackEndModel(programFrontEndModel)],
  };

  return await create(academicBackEndModel);
}

async function updateProgram(formData, update) {
  const programFrontEndModel = mergeProgramSteps(formData);

  const programBackEndModel = parseToProgramBackEndModel(programFrontEndModel);

  return await update(programBackEndModel);
}

async function getViewData(fetchOne, fetchAllBranches, programId) {
  const branchesPromise = requestCache<any>("branches", fetchAllBranches);
  const [data, branches] = await Promise.all([
    fetchOne(programId),
    branchesPromise,
  ]);

  const previewData = parseToProgramPreviewSecondary({
    backEndData: data.data,
    branches: branches.data,
  });

  return {
    injectedData: {
      data: previewData,
    },
  };
}

async function getEditData(fetchOne, programId) {
  const { data } = await fetchOne(programId);
  return parseToEditProgramInitialDataSecondary(data);
}

async function getApplicationsCount(fetch, id) {
  const { data } = await fetch({ programId: id });

  return {
    injectedData: {
      approved: data.approved,
      pending: data.pending,
    },
  };
}

export function useProgramConfigSecondary(): IProgramWizardConfigs {
  const {
    create,
    update,
    activate,
    deactivate,
    deleteOne,
    checkDuplication,
    fetchOne,
    fetchAppCount,
  } = useProgramsQueriesSecondary();
  const { fetchAll: fetchAllBranches } = useBranchQueries();
  const schoolId = useSchoolId();

  const fetchAllBranchesModified = () => fetchAllBranches({ schoolId });

  const getCreateConfig = (academicId): IWizardConfig => ({
    steps: [
      { ...createConfigStep1, backendValidator: checkDuplication },
      createConfigStep2,
      createConfigStep3,
      createConfigStep4,
      createConfigStep5,
    ],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Create,
    getInitialData: () => getCreateInitialData(academicId),
    saveCallback: (data) => createProgram(data, create),
  });

  const getViewConfig = (id): IWizardConfig => ({
    steps: [viewContentConfig],
    submitButtonText: null,
    cancelButtonText: "Close",
    mode: WizardMode.View,
    getInitialData: () => getViewData(fetchOne, fetchAllBranchesModified, id),
  });

  const getEditConfig = (id): IWizardConfig => ({
    steps: [
      { ...editConfigStep1, backendValidator: checkDuplication },
      editConfigStep2,
      editConfigStep3,
      editConfigStep4,
    ],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Edit,
    getInitialData: () => getEditData(fetchOne, id),
    saveCallback: (data) => updateProgram(data, update),
  });

  const getActivateConfig = (id): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          Icon: Activate,
          titleText: "Activate program",
        },
        content: `Are you sure you want to activate this program?`,
      },
    ],
    submitButtonText: "Activate",
    cancelButtonText: "Close",
    saveCallback: () => activate(id),
  });

  const getDeactivateConfig = (id): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          titleText: "Deactivate program",
        },
        width: "552px",
        content: () => <DeactivateContent name={ENTITY_NAMES.program} />,
        validator: deactivateFormValidator,
      },
    ],
    submitButtonText: "Deactivate",
    submitButtonColor: SubmitButtonColor.Danger,
    cancelButtonText: "Close",
    saveCallback: (data) =>
      deactivate(id, {
        text: data?.reason ?? "",
      }),
    getInitialData: () => getApplicationsCount(fetchAppCount, id),
  });

  const getDeleteConfig = (id): IWizardConfig => ({
    steps: [
      {
        width: "430px",
        titleConfig: {
          titleText: "Delete program",
        },
        content: () => <DeleteContent name={ENTITY_NAMES.program} />,
      },
    ],
    mode: WizardMode.Delete,
    getInitialData: () => getApplicationsCount(fetchAppCount, id),
    saveCallback: () => deleteOne(id),
    getButtonConfigs: getDeleteButtonConfigs,
  });

  return {
    getCreateConfig,
    getViewConfig,
    getEditConfig,
    getActivateConfig,
    getDeactivateConfig,
    getDeleteConfig,
  };
}
